/* eslint-disable react/prop-types, prettier/prettier */
import React, { useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import BlockIcon from '@mui/icons-material/Block'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import SaveIcon from '@mui/icons-material/Save'
import SendIcon from '@mui/icons-material/Send'
import { keyframes } from '@emotion/react'
import { useNavigate, useLocation } from 'react-router-dom'
import AIService from '../../services/aiService'
import { channelFrom, channelLabel } from '../../helpers/aiHandoff'
import { useToolRun } from '../../helpers/toolRun'
import CopyButton from '../../components/shared/CopyButton'
import ErrorBanner from '../../components/shared/ErrorBanner'
import {
    ACCENT,
    FONT,
    GRADIENT,
    ICON,
    ON_ORANGE,
    RADIUS,
    STATUS,
    SURFACE,
    TEXT,
    menuProps,
    pageSx,
    cardSx,
} from '../../theme/tokens'

const fadeIn = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }`

const CHANNELS = ['whatsapp', 'email', 'sms']

// Each risk level is a STATUS pair: low success, medium warning, high error.
export const RISK_CONFIG = {
    low: {
        color: STATUS.success.fg,
        bg: STATUS.success.bg,
        border: STATUS.success.border,
        label: 'Low Risk',
        Icon: CheckCircleIcon,
    },
    medium: {
        color: STATUS.warning.fg,
        bg: STATUS.warning.bg,
        border: STATUS.warning.border,
        label: 'Medium Risk',
        Icon: WarningAmberIcon,
    },
    high: {
        color: STATUS.error.fg,
        bg: STATUS.error.bg,
        border: STATUS.error.border,
        label: 'High Risk',
        Icon: BlockIcon,
    },
}

// A risk factor's severity, the same tones; unknown ones take the orange
// accent.
export const SEVERITY_TONE = {
    low: STATUS.success,
    medium: STATUS.warning,
    high: STATUS.error,
}
const SEVERITY_FALLBACK = {
    fg: ACCENT.text,
    bg: ACCENT.bg12,
    border: ACCENT.border30,
}

// A sample message for "Use example": filled in only when the user asks.
export const EXAMPLE_TEMPLATE =
    'Hi {{name}}! 👋\n\nYour *{{batch_name}}* session starts in *30 minutes*.\n\nJoin here 👇\n{{join_link}}\n\nSee you there!\n— Team Bhanzu'

function ScoreGauge({ score, riskLevel }) {
    const cfg = RISK_CONFIG[riskLevel] || RISK_CONFIG.low
    const radius = 52
    const circ = 2 * Math.PI * radius
    const dash = (score / 100) * circ
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
            }}
        >
            <Box sx={{ position: 'relative', width: 140, height: 140 }}>
                <svg
                    width="140"
                    height="140"
                    style={{ transform: 'rotate(-90deg)' }}
                >
                    {/* Colours as style: a CSS variable needs a CSS property,
    not an SVG attribute. */}
                    <circle
                        cx="70"
                        cy="70"
                        r={radius}
                        fill="none"
                        strokeWidth="10"
                        style={{ stroke: SURFACE.sunken }}
                    />
                    <circle
                        cx="70"
                        cy="70"
                        r={radius}
                        fill="none"
                        strokeWidth="10"
                        strokeDasharray={`${dash} ${circ - dash}`}
                        strokeLinecap="round"
                        style={{
                            stroke: cfg.color,
                            transition: 'stroke-dasharray 0.8s ease',
                        }}
                    />
                </svg>
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Typography
                        variant="h1"
                        sx={{ color: cfg.color, lineHeight: 1 }}
                    >
                        {score}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{ color: TEXT.secondary, mt: 0.25 }}
                    >
                        / 100
                    </Typography>
                </Box>
            </Box>
            <Chip
                icon={
                    <cfg.Icon
                        sx={{
                            fontSize: `${ICON.sm}px !important`,
                            color: `${cfg.color} !important`,
                        }}
                    />
                }
                label={cfg.label}
                sx={{
                    background: cfg.bg,
                    color: cfg.color,
                    border: `1px solid ${cfg.border}`,
                    fontWeight: 700,
                    typography: 'caption',
                }}
            />
        </Box>
    )
}

export default function UnsubscribeRisk() {
    const [templateBody, setTemplateBody] = useState('')
    const [channel, setChannel] = useState('whatsapp')
    const [loading, setLoading] = useState(false)
    const [result, setResult] = useState(null)
    const [error, setError] = useState('')

    const navigate = useNavigate()
    const location = useLocation()
    const run = useToolRun('unsubscribe-risk')

    useEffect(() => {
        // Reset outputs so re-runs (navigation with new state) behave predictably
        setResult(null)
        setError('')
        const saved = run.recall()
        if (saved) {
            setTemplateBody(saved.templateBody ?? '')
            setChannel(saved.channel)
            setResult(saved.result)
            return
        }
        const state = location.state
        // Only a template handed over by another page fills the input; it
        // used to start with a sample message, sent as if it were the user's.
        setTemplateBody(state?.template_body ?? '')
        if (state?.channel) {
            setChannel(channelFrom(state.channel, CHANNELS, 'whatsapp'))
        }
    }, [location.state])

    const handleAnalyse = async () => {
        if (!templateBody.trim()) return
        setLoading(true)
        setError('')
        setResult(null)
        run.forget()
        try {
            const res = await AIService.unsubscribeRisk({
                template_body: templateBody,
                channel,
            })
            const analysed = res.data?.result
            setResult(analysed)
            if (analysed)
                run.remember({ templateBody, channel, result: analysed })
        } catch {
            setError('Failed to analyse template. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box sx={pageSx}>
            <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 3, md: 5 } }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 3,
                    }}
                >
                    <Box
                        sx={{
                            background: GRADIENT.brand,
                            borderRadius: `${RADIUS.md}px`,
                            p: 1,
                            display: 'flex',
                        }}
                    >
                        {/* The AI tools' brand gradient with a Deep Blue mark (white was
    under 3:1 on the old red-to-amber gradient's amber). */}
                        <BlockIcon
                            sx={{ color: ON_ORANGE, fontSize: ICON.lg }}
                        />
                    </Box>
                    <Box>
                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{ color: TEXT.primary }}
                        >
                            Unsubscribe Risk
                        </Typography>
                        <Typography
                            variant="caption"
                            component="p"
                            sx={{ color: TEXT.secondary }}
                        >
                            Analyse a template for opt-out risk factors before
                            sending
                        </Typography>
                    </Box>
                </Box>

                {/* Form */}
                <Paper elevation={0} sx={{ ...cardSx, p: 2.5, mb: 3 }}>
                    <TextField
                        select
                        label="Channel"
                        value={channel}
                        onChange={(e) => setChannel(e.target.value)}
                        size="small"
                        SelectProps={{ MenuProps: menuProps }}
                        sx={{ width: 200, mb: 2 }}
                    >
                        {CHANNELS.map((c) => (
                            <MenuItem key={c} value={c}>
                                {channelLabel(c)}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            mb: 1,
                        }}
                    >
                        <Button
                            variant="text"
                            size="small"
                            onClick={() => setTemplateBody(EXAMPLE_TEMPLATE)}
                            sx={{ textTransform: 'none' }}
                        >
                            Use example
                        </Button>
                    </Box>
                    <TextField
                        label="Paste your message template"
                        value={templateBody}
                        onChange={(e) => setTemplateBody(e.target.value)}
                        multiline
                        rows={7}
                        fullWidth
                        placeholder={
                            'Hi {{name}}! 👋\n\nYour *{{batch_name}}* session starts in *30 minutes*.\n\nJoin here 👇\n{{join_link}}\n\n— Team Bhanzu'
                        }
                    />

                    <Button
                        variant="contained"
                        fullWidth
                        onClick={handleAnalyse}
                        disabled={loading || !templateBody.trim()}
                        startIcon={
                            loading ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <AutoAwesomeIcon />
                            )
                        }
                        sx={{ mt: 2, py: 1.25 }}
                    >
                        {loading ? 'Analysing…' : 'Analyse Risk'}
                    </Button>
                </Paper>

                {error && (
                    <Box sx={{ mb: 2 }}>
                        <ErrorBanner message={error} />
                    </Box>
                )}

                {/* Results */}
                {result && (
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            animation: `${fadeIn} 0.3s ease`,
                        }}
                    >
                        {/* Score + summary row */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'auto 1fr',
                                },
                                gap: 2,
                                alignItems: 'start',
                            }}
                        >
                            <Paper
                                elevation={0}
                                sx={{
                                    ...cardSx,
                                    p: 3,
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                            >
                                <ScoreGauge
                                    score={result.score}
                                    riskLevel={result.risk_level}
                                />
                            </Paper>

                            {/* Risk factors */}
                            <Paper elevation={0} sx={{ ...cardSx, p: 2.5 }}>
                                <Typography
                                    variant="overline"
                                    component="p"
                                    sx={{ color: TEXT.muted, mb: 1.5 }}
                                >
                                    Risk Factors
                                </Typography>
                                {result.risk_factors &&
                                result.risk_factors.length > 0 ? (
                                    result.risk_factors.map((rf, i) => {
                                        const sev =
                                            SEVERITY_TONE[rf.severity] ||
                                            SEVERITY_FALLBACK
                                        return (
                                            <Box
                                                key={i}
                                                sx={{
                                                    display: 'flex',
                                                    gap: 1.5,
                                                    mb: 1.25,
                                                    pb: 1.25,
                                                    borderBottom:
                                                        i <
                                                        result.risk_factors
                                                            .length -
                                                            1
                                                            ? `1px solid ${SURFACE.border}`
                                                            : 'none',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 6,
                                                        height: 6,
                                                        borderRadius: '50%',
                                                        background: sev.fg,
                                                        mt: '6px',
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Box>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                            gap: 1,
                                                            mb: 0.25,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: TEXT.primary,
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {rf.factor}
                                                        </Typography>
                                                        <Chip
                                                            label={rf.severity}
                                                            size="small"
                                                            sx={{
                                                                background:
                                                                    sev.bg,
                                                                color: sev.fg,
                                                                border: `1px solid ${sev.border}`,
                                                                typography:
                                                                    'caption',
                                                                height: 18,
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography
                                                        variant="caption"
                                                        component="p"
                                                        sx={{
                                                            color: TEXT.muted,
                                                            lineHeight: 1.5,
                                                        }}
                                                    >
                                                        {rf.detail}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )
                                    })
                                ) : (
                                    <Typography
                                        variant="body2"
                                        sx={{ color: TEXT.muted }}
                                    >
                                        No risk factors detected.
                                    </Typography>
                                )}
                            </Paper>
                        </Box>

                        {/* Suggestions */}
                        <Paper
                            elevation={0}
                            sx={{
                                ...cardSx,
                                border: `1px solid ${STATUS.warning.border}`,
                                p: 2.5,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 1.5,
                                }}
                            >
                                <LightbulbIcon
                                    sx={{
                                        color: STATUS.warning.fg,
                                        fontSize: ICON.md,
                                    }}
                                />
                                <Typography
                                    variant="overline"
                                    sx={{ color: TEXT.muted }}
                                >
                                    Suggestions
                                </Typography>
                            </Box>
                            {(result.suggestions ?? []).map((s, i) => (
                                <Box
                                    key={i}
                                    sx={{ display: 'flex', gap: 1, mb: 0.75 }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: STATUS.warning.fg,
                                            mt: '1px',
                                        }}
                                    >
                                        •
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: TEXT.secondary,
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        {s}
                                    </Typography>
                                </Box>
                            ))}
                        </Paper>

                        {/* Revised snippet */}
                        {result.revised_snippet && (
                            <Paper
                                elevation={0}
                                sx={{
                                    background: STATUS.success.bg,
                                    border: `1px solid ${STATUS.success.border}`,
                                    borderRadius: `${RADIUS.lg}px`,
                                    p: 2.5,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 1.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <CheckCircleIcon
                                            sx={{
                                                color: STATUS.success.fg,
                                                fontSize: ICON.md,
                                            }}
                                        />
                                        <Typography
                                            variant="overline"
                                            sx={{ color: TEXT.muted }}
                                        >
                                            Revised Snippet
                                        </Typography>
                                    </Box>
                                    <CopyButton text={result.revised_snippet} />
                                </Box>
                                <Box
                                    sx={{
                                        background: SURFACE.sunken,
                                        borderRadius: `${RADIUS.sm}px`,
                                        p: 1.75,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: TEXT.secondary,
                                            whiteSpace: 'pre-wrap',
                                            fontFamily: FONT.mono,
                                            lineHeight: 1.7,
                                        }}
                                    >
                                        {result.revised_snippet}
                                    </Typography>
                                </Box>
                            </Paper>
                        )}

                        {/* CTA row */}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                mt: 2,
                                flexWrap: 'wrap',
                            }}
                        >
                            {result.revised_snippet && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<SaveIcon />}
                                    onClick={() =>
                                        navigate('/create-template', {
                                            state: {
                                                body: result.revised_snippet,
                                                channel: channel,
                                            },
                                        })
                                    }
                                >
                                    Save Revised Version
                                </Button>
                            )}
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<SendIcon />}
                                onClick={() =>
                                    navigate('/bulk-send', {
                                        state: { template_body: templateBody },
                                    })
                                }
                            >
                                Proceed to Bulk Send
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    )
}
